import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useRouterState } from '@tanstack/react-router';
import { useHotkey, useHotkeySequences } from '@tanstack/react-hotkeys';
import { annotationsOf } from '@trove/core';
import { ActionMenu, Button } from '@trove/ui';
import { CaretDown, Clock, Files, FloppyDisk, GearSix, Users } from '@phosphor-icons/react';
import { pendingChangeLabel, pendingChanges } from '../commitChanges';
import { parentFolder } from '../fileTree';
import { useSession } from '../session';
import { useWorkspace } from '../workspace';
import { Brand } from './Brand';
import { ProfileAvatar } from './ProfileAvatar';
import { SidebarAnnouncements } from './SidebarAnnouncements';
import { SidebarFileTree } from './SidebarFileTree';
import { VaultSearch } from './VaultSearch';
import { VaultSwitcher } from './VaultSwitcher';
import styles from './AppShell.module.css';

const navItems = [
  { to: '/', label: 'Files', icon: Files, exact: true },
  { to: '/shared', label: 'Shared', icon: Users },
] as const;

const mobileNavItems = [
  { to: '/', label: 'Files', icon: Files, exact: true },
  { to: '/shared', label: 'Shared', icon: Users },
] as const;

const titles: Record<string, string> = {
  '/': 'Repository',
  '/history': 'Version history',
  '/shared': 'Shared with me',
  '/connect': 'Connect device',
  '/device': 'Connect device',
  '/settings': 'Settings',
};

const titleIcons = {
  '/': Files,
  '/history': Clock,
  '/shared': Users,
  '/connect': GearSix,
  '/device': GearSix,
  '/settings': GearSix,
} as const;

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout, resetDemo } = useSession();
  const { workspace, syncing, dirty, saveMode, commitPending, discardPending } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const search = useRouterState({
    select: (state) => state.location.search as { folder?: string; file?: string },
  });
  const sharedRouteAllowed =
    location.pathname === '/shared' ||
    location.pathname === '/device' ||
    location.pathname === '/connect' ||
    location.pathname === '/settings';
  const filesRoute = location.pathname === '/';
  const nestedInTree = Boolean(user) && filesRoute && Boolean(search.folder || search.file);
  const showPending = dirty && saveMode === 'queued';
  const pending = useMemo(() => {
    if (!workspace?.pendingBaseline) return [];
    return pendingChanges(workspace.snapshot.files, workspace.pendingBaseline.files);
  }, [workspace?.pendingBaseline, workspace?.snapshot.files]);

  useHotkey('Mod+S', () => void commitPending(), {
    enabled: showPending && !syncing,
    meta: { name: 'Save changes', description: 'Save queued workspace changes' },
  });

  useHotkeySequences(
    [
      {
        sequence: ['G', 'F'],
        callback: () => void navigate({ to: '/', search: {} }),
        options: {
          enabled: workspace?.mode === 'owner',
          meta: { name: 'Go to files', description: 'Open the root files view' },
        },
      },
      {
        sequence: ['G', 'S'],
        callback: () => void navigate({ to: '/shared' }),
        options: {
          enabled: Boolean(user),
          meta: { name: 'Go to shared', description: 'Open shared collections' },
        },
      },
      {
        sequence: ['G', 'H'],
        callback: () => void navigate({ to: '/history' }),
        options: {
          enabled: workspace?.mode === 'owner',
          meta: { name: 'Go to history', description: 'Open version history' },
        },
      },
      {
        sequence: ['G', 'T'],
        callback: () => void navigate({ to: '/settings' }),
        options: {
          enabled: Boolean(user),
          meta: { name: 'Go to settings', description: 'Open settings' },
        },
      },
      {
        sequence: ['G', 'C'],
        callback: () => void navigate({ to: '/connect' }),
        options: {
          enabled: workspace?.mode === 'owner',
          meta: { name: 'Go to connect', description: 'Open Connect device' },
        },
      },
    ],
    { ignoreInputs: true, timeout: 800 },
  );

  const accountItems = [
    { label: 'Settings', onClick: () => void navigate({ to: '/settings' }) },
    ...(user?.demo ? [{ label: 'Reset demo', onClick: () => void resetDemo() }] : []),
    { label: 'separator', onClick: () => {}, separator: true as const },
    { label: 'Sign out', onClick: () => void logout(), danger: true as const },
  ];
  useEffect(() => {
    if (workspace?.mode === 'shared' && !sharedRouteAllowed) {
      void navigate({ to: '/shared' });
    }
  }, [workspace?.mode, sharedRouteAllowed, navigate]);

  if (!user) return null;

  const accountHeader = (
    <div className={styles.accountMenuIdentity}>
      <span className={styles.accountMenuName}>{user.displayName}</span>
      <span className={styles.accountMenuEmail}>{user.email}</span>
    </div>
  );

  if (workspace?.mode === 'shared') {
    return (
      <div className={styles.sharedApp}>
        <header className={styles.sharedTopbar}>
          <div className={styles.sharedTopbarInner}>
            <Brand />
            <div className={styles.sharedTopActions}>
              <span className={styles.syncStatus}>
                <span className={`${styles.syncDot} ${syncing ? styles.syncDotBusy : ''}`} />
                <span>{syncing ? 'Opening securely…' : 'Decrypted on this device'}</span>
              </span>
              <ActionMenu
                trigger={
                  <button
                    className={styles.sharedAccountButton}
                    type="button"
                    aria-label="Account menu"
                  >
                    <ProfileAvatar user={user} className={styles.avatar} />
                    <CaretDown size={12} />
                  </button>
                }
                header={accountHeader}
                items={accountItems}
              />
            </div>
          </div>
        </header>
        <main className={styles.sharedMain}>{sharedRouteAllowed ? children : null}</main>
      </div>
    );
  }

  const TitleIcon = titleIcons[location.pathname as keyof typeof titleIcons] ?? Files;
  const pendingControls = showPending ? (
    <div className={styles.pendingBar}>
      <div className={styles.pendingCopy}>
        <span className={styles.pendingLabel}>
          {pending.length === 0
            ? 'Unsaved changes'
            : pending.length === 1
              ? '1 unsaved change'
              : `${pending.length} unsaved changes`}
        </span>
        {pending.length > 0 ? (
          <ul className={styles.pendingList}>
            {pending.map((change) => (
              <li key={`${change.status}:${change.path}`} className={styles.pendingItem}>
                <span className={styles.pendingPath} title={change.path}>
                  {change.path}
                </span>
                <span className={styles.pendingStatus}>{pendingChangeLabel(change.status)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.pendingEmpty}>Local edits waiting to sync.</p>
        )}
      </div>
      <div className={styles.pendingActions}>
        <Button
          size="small"
          variant="ghost"
          disabled={syncing}
          onClick={() => void discardPending()}
        >
          Discard
        </Button>
        <Button
          size="small"
          variant="primary"
          disabled={syncing}
          aria-keyshortcuts="Meta+S Control+S"
          onClick={() => void commitPending()}
        >
          <FloppyDisk size={12} />
          Save
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <div className={`${styles.app} ${showPending ? styles.appPending : ''}`}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarBrand}>
          <Link to="/" search={{}} className={styles.brandHomeLink} aria-label="Go to home">
            <Brand />
          </Link>
          <VaultSwitcher />
        </div>
        <div className={styles.desktopSearch}>
          <VaultSearch
            onOpenPath={(path) =>
              void navigate({ to: '/', search: { folder: parentFolder(path), file: path } })
            }
          />
        </div>
        <div className={styles.sidebarPanels}>
          <div
            className={`${styles.sidebarPanel} ${styles.sidebarPanelNav} ${
              nestedInTree ? styles.sidebarPanelHidden : styles.sidebarPanelActive
            }`}
            aria-hidden={nestedInTree}
          >
            <nav className={styles.nav} aria-label="Main navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    tabIndex={nestedInTree ? -1 : undefined}
                    activeOptions={{
                      exact: 'exact' in item ? item.exact : false,
                      includeSearch: false,
                    }}
                    className={styles.navLink}
                    activeProps={{ className: styles.navLinkActive }}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <SidebarAnnouncements inert={nestedInTree} />
          </div>
          {workspace ? (
            <div
              className={`${styles.sidebarPanel} ${styles.sidebarPanelExplorer} ${
                nestedInTree ? styles.sidebarPanelActive : styles.sidebarPanelHidden
              }`}
              aria-hidden={!nestedInTree}
            >
              <div className={styles.explorerHeader}>
                <span className={styles.navLabel}>Explorer</span>
                <Link
                  to="/shared"
                  className={styles.explorerShared}
                  tabIndex={nestedInTree ? undefined : -1}
                >
                  <Users size={12} />
                  Shared
                </Link>
              </div>
              <SidebarFileTree
                files={workspace.snapshot.files}
                folder={search.folder}
                file={search.file}
                annotations={annotationsOf(workspace.snapshot)}
              />
            </div>
          ) : null}
        </div>
        <nav className={styles.mobileNav} aria-label="Main navigation">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{
                  exact: 'exact' in item ? item.exact : false,
                  includeSearch: false,
                }}
                className={`${styles.navLink} ${styles.mobileNavLink}`}
                activeProps={{
                  className: `${styles.navLink} ${styles.mobileNavLink} ${styles.navLinkActive}`,
                }}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className={styles.sidebarFooter}>
          {pendingControls}
          <ActionMenu
            trigger={
              <button className={styles.accountButton} type="button">
                <ProfileAvatar user={user} className={styles.avatar} />
                <span>
                  <span className={styles.accountName}>{user.displayName}</span>
                  <span className={styles.accountEmail}>{user.email}</span>
                </span>
                <CaretDown size={12} />
              </button>
            }
            header={accountHeader}
            items={accountItems}
          />
        </div>
      </aside>
      {showPending ? <div className={styles.mobilePendingDock}>{pendingControls}</div> : null}
      <main className={styles.main}>
        <header className={`${styles.topbar} ${filesRoute ? styles.topbarFiles : ''}`}>
          <div className={styles.topTitle}>
            <span className={styles.mobileBrand}>
              <Link to="/" search={{}} className={styles.brandHomeLink} aria-label="Go to home">
                <Brand />
              </Link>
            </span>
            <span className={styles.mobileVaultSwitcher}>
              <VaultSwitcher />
            </span>
            <span className={styles.desktopTitleIcon}>
              <TitleIcon size={16} />
            </span>
            <span className={styles.desktopTitleText}>{titles[location.pathname] ?? 'Trove'}</span>
          </div>
          {filesRoute ? (
            <div className={styles.mobileSearch}>
              <VaultSearch
                variant="mobile"
                onOpenPath={(path) =>
                  void navigate({ to: '/', search: { folder: parentFolder(path), file: path } })
                }
              />
            </div>
          ) : null}
          <div className={styles.topActions}>
            <ActionMenu
              trigger={
                <button
                  className={styles.mobileAccountButton}
                  type="button"
                  aria-label="Account menu"
                >
                  <ProfileAvatar user={user} className={styles.mobileAvatar} />
                </button>
              }
              header={accountHeader}
              items={accountItems}
            />
          </div>
        </header>
        <div className={`${styles.content} ${filesRoute ? styles.contentExplorer : ''}`}>
          {children}
        </div>
      </main>
    </div>
  );
}
